import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import Api, { DEFAULT_ERROR_MESSAGE } from '../api/Api';
import { IPlotVariable } from '../types/analytics';
import {
  Dfs2SpectralResponse,
  IWaveSpectraParams,
  IWaveSpectraTimeseries,
} from '../types/waveSpectra';
import { DT0Format } from '../utils/StaticVariable';
import { QUERY_KEYS } from './queryKeys';

interface IWaveSpectraTimeseriesArgs {
  spectraData: Dfs2SpectralResponse | undefined;
  params: IWaveSpectraParams | null;
  orderId: string;
  pointName: string;
  variable: IPlotVariable | null;
}

export function useGetWaveSpectraTimeseriesQuery({
  spectraData,
  params,
  orderId,
  pointName,
  variable,
}: IWaveSpectraTimeseriesArgs) {
  const times = spectraData?.metadata.times;
  const startDate = times?.length ? moment(times[0]).format(DT0Format) : null;
  const endDate = times?.length ? moment(times[times.length - 1]).format(DT0Format) : null;

  return useQuery<IWaveSpectraTimeseries | null>({
    queryKey: [
      QUERY_KEYS.waveSpectra.timeseries,
      params?.datasetId,
      params?.longitude,
      params?.latitude,
      startDate,
      endDate,
      variable?.Name,
    ],
    queryFn: async () => {
      if (!params || !variable || !startDate || !endDate) {
        return null;
      }

      const data = await Api.chart.getBasicChartV2(
        orderId,
        pointName,
        params.datasetId,
        startDate,
        endDate,
        `${params.longitude},${params.latitude}`,
        60,
        variable.Name
      );

      if (!data) {
        throw new Error(DEFAULT_ERROR_MESSAGE);
      }

      if (data.Error) {
        throw new Error(
          typeof data.Error === 'string' ? data.Error : DEFAULT_ERROR_MESSAGE
        );
      }

      const items: Array<{ [key: string]: any }> | undefined = data.Data?.Items;
      if (!items?.length) {
        return null;
      }

      const dataKey = variable.Name.split(' ').join('_');
      return {
        times: items.map((item) => item.Time),
        values: items.map((item) => item[dataKey]),
        name: variable.Name,
        shortName: variable.ShortName ?? variable.Name,
        unit: variable.Unit ?? '',
      };
    },
    enabled: !!params && !!variable && !!startDate && !!endDate,
  });
}
